/**
 * glassPipeline — a continuous wavy glass sheet covering the viewport.
 *
 * One mechanism: a procedural glass sheet with a wavy height field
 * that bends light through it. The world behind the glass is a
 * photographic sky+ground scene with a few bright sun-like lights.
 *
 * The glass is computed in three passes per pixel:
 *   1. Refraction: the height gradient defines a refraction vector;
 *      we sample the world behind the glass at a UV offset by that
 *      vector. (Light bending through the glass.)
 *   2. Reflection: a reflection vector at the surface; we sample the
 *      world in that direction. (Mirror reflection from the sky.)
 *   3. Fresnel blend: at grazing angles (large height gradient), the
 *      reflection dominates; at perpendicular, the refraction dominates.
 *      Mix( refraction, reflection, fresnel ) is the final color.
 *   4. Polish: a specular highlight where the view direction matches
 *      the perfect reflection of a key light source.
 *
 * The world behind the glass is procedurally rendered: a deep-twilight
 * sky with a horizon glow, a ground plane, and 3 bright sun-like
 * spheres that produce the specular highlights and focused caustics.
 *
 * No DOM overlays, no entry animation — one immersive thing.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface GlassPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}
const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
float bass; float mid; float high;

const float TAU = 6.283185307179586;
const float PI = 3.14159265358979;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * vnoise(p);
    p = p * 2.05 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

// === Glass surface height field ===
// The height is a sum of radial sine waves + FBM micro-detail.
// This is the "wavy" sheet — like a frozen waterfall.
float glassHeight(vec2 p, float t) {
  // Big waves: layered radial sines.
  float h = 0.0;
  h += 0.10 * sin(p.x * 4.0 + t * 0.7);
  h += 0.08 * sin(p.y * 5.0 - t * 0.5);
  h += 0.06 * sin((p.x + p.y) * 3.5 + t * 0.4);
  h += 0.05 * sin(length(p - vec2(0.3, 0.2)) * 6.0 - t * 0.9);
  h += 0.04 * sin(length(p + vec2(0.4, -0.3)) * 7.0 + t * 0.6);
  // Micro-ripples: FBM-driven pinpricks of distortion.
  float micro = fbm(p * 8.0 + vec2(t * 0.3, -t * 0.2)) - 0.5;
  h += micro * 0.05;
  // Slow undulation.
  h += 0.03 * sin(t * 0.2 + fbm(p * 1.5) * 6.0);
  return h;
}

// === Glass surface normal ===
// The gradient of the height field, sampled via finite differences.
vec2 glassNormal(vec2 p, float t) {
  float h = 0.005;
  float dx = (glassHeight(p + vec2(h, 0.0), t) - glassHeight(p - vec2(h, 0.0), t)) / (2.0 * h);
  float dy = (glassHeight(p + vec2(0.0, h), t) - glassHeight(p - vec2(0.0, h), t)) / (2.0 * h);
  return vec2(dx, dy);
}

// === World scene ===
// A photographic sky+ground with bright sun-like lights. We sample
// this either at a UV (for refraction) or at a direction (for
// reflection). The "sky" is a vertical gradient with a strong
// horizon glow; the "ground" is a darker uniform gradient.
vec3 skyColor(vec3 dir) {
  // Y is up. Above 0 = sky, below 0 = ground.
  float y = dir.y;
  // Sky gradient: deep twilight blue at the top, warm horizon glow.
  vec3 zenith = vec3(0.04, 0.06, 0.14);
  vec3 horizon = vec3(0.85, 0.45, 0.30);
  float skyMix = smoothstep(-0.1, 0.6, y);
  vec3 sky = mix(horizon, zenith, skyMix);
  // Ground: warm desert sand.
  vec3 ground = mix(vec3(0.18, 0.10, 0.06), vec3(0.02, 0.02, 0.04), smoothstep(-0.4, 0.0, y));
  return mix(ground, sky, smoothstep(-0.05, 0.05, y));
}

// A bright sun-like point light at a fixed direction.
vec3 sunLight(vec3 dir) {
  vec3 sunDir = normalize(vec3(0.5, 0.6, -0.7));
  float c = max(0.0, dot(dir, sunDir));
  // A tight core + a soft halo.
  float core = pow(c, 200.0);
  float halo = pow(c, 16.0) * 0.4;
  return vec3(1.0, 0.85, 0.55) * core * 6.0 + vec3(1.0, 0.7, 0.4) * halo;
}

// Two smaller accent lights for sparkle.
vec3 accentLight(vec3 dir, vec3 pos, float sharpness) {
  vec3 d = normalize(dir);
  float c = max(0.0, dot(d, normalize(pos)));
  return vec3(0.6, 0.85, 1.0) * pow(c, sharpness) * 3.0;
}

// Sample the world either in a 2D UV (refraction) or a 3D direction (reflection).
vec3 worldSampleUV(vec2 uv) {
  // Map uv to a direction: assume the world is at infinity in front
  // of the camera, so uv.x/y maps to a direction (x, y, -1) normalized.
  vec3 dir = normalize(vec3((uv - 0.5) * 2.0, -1.0));
  return skyColor(dir) + sunLight(dir) +
         accentLight(dir, vec3(-0.7, 0.4, 0.5), 120.0) +
         accentLight(dir, vec3(0.6, -0.2, 0.3), 80.0);
}

vec3 worldSampleDir(vec3 dir) {
  return skyColor(dir) + sunLight(dir) +
         accentLight(dir, vec3(-0.7, 0.4, 0.5), 120.0) +
         accentLight(dir, vec3(0.6, -0.2, 0.3), 80.0);
}

// === Caustics on the world behind the glass ===
// The glass surface bends light. Where the curvature is high, light
// gets focused — those become bright caustic spots on the ground.
// We approximate this by sampling a high-frequency pattern with
// modulation by the height gradient magnitude.
vec3 causticsAt(vec2 uv, vec2 n) {
  // Curvature = magnitude of the height gradient.
  float curvature = length(n);
  // Caustic pattern: rotated FBM, thresholded at a high iso-level
  // so caustics are RARE bright spots, not a snowy texture.
  vec2 p = uv * 30.0;
  float c = 0.0;
  float a = 0.41;
  mat2 R = mat2(cos(a), -sin(a), sin(a), cos(a));
  p = R * p;
  c += vnoise(p * 1.3);
  c += vnoise(p * 2.4 + 7.1);
  c += vnoise(p * 4.5 - 3.7);
  // High threshold: only the brightest ridges survive.
  float ring = smoothstep(2.35, 2.55, c);
  // Multiply by curvature so caustics only appear where the glass
  // is actively bending.
  return vec3(1.0, 0.95, 0.85) * ring * curvature * 3.0;
}

void main() {
  vec2 uv = vUV;
  float T = uTime;

  // Aggregate audio.
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid  = (uMid.x  + uMid.y  + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Map uv to a sheet coord with letterboxing for aspect ratio.
  vec2 p = (uv - 0.5) * 2.0;
  p.x *= uResolution.x / uResolution.y;

  // Glass surface: height + normal.
  float h = glassHeight(p, T);
  vec2 n = glassNormal(p, T);

  // === Refraction ===
  // The refraction vector is the height gradient, scaled by a
  // refractive-index-like factor. The offset is large enough to
  // bend the world visibly.
  float REFRACT_STRENGTH = 0.25;
  vec2 refr = n * REFRACT_STRENGTH;
  // Subtle chromatic aberration: the three color channels refract
  // slightly differently (dispersion).
  vec3 refrCol;
  refrCol.r = worldSampleUV(uv - refr * 1.10).r;
  refrCol.g = worldSampleUV(uv - refr * 1.00).g;
  refrCol.b = worldSampleUV(uv - refr * 0.90).b;

  // === Reflection ===
  // The reflection vector is the view direction reflected across the
  // surface normal. We treat the view as (-0, -0, -1) (looking into
  // the screen) and the normal as a perturbed direction.
  vec3 viewDir = vec3(0.0, 0.0, 1.0);
  vec3 normal3 = normalize(vec3(-n.x, -n.y, 1.0));
  vec3 reflDir = reflect(-viewDir, normal3);
  vec3 reflCol = worldSampleDir(reflDir);

  // === Fresnel blend ===
  // At grazing angles (large height gradient), reflection dominates.
  // At perpendicular (small gradient), refraction dominates.
  float angle = max(0.0, 1.0 - dot(normal3, viewDir));
  float fresnel = pow(0.04 + 0.96 * angle, 5.0);
  // Audio modulates the fresnel — beats make the glass read as
  // more reflective.
  fresnel = clamp(fresnel + bass * 0.05, 0.0, 1.0);

  // === Mix refraction + reflection ===
  vec3 col = mix(refrCol, reflCol, fresnel);

  // === Specular highlight ===
  // Three specular hotspots with different sharpness and color so
  // the polish looks varied — two sun reflections and an accent.
  vec3 sunDir1 = normalize(vec3(0.5, 0.6, -0.7));
  vec3 sunDir2 = normalize(vec3(-0.4, 0.7, 0.3));
  vec3 sunDir3 = normalize(vec3(0.0, -0.3, 0.9));
  vec3 halfDir1 = normalize(sunDir1 + viewDir);
  vec3 halfDir2 = normalize(sunDir2 + viewDir);
  vec3 halfDir3 = normalize(sunDir3 + viewDir);
  float spec1 = pow(max(0.0, dot(normal3, halfDir1)), 200.0);
  float spec2 = pow(max(0.0, dot(normal3, halfDir2)), 120.0);
  float spec3 = pow(max(0.0, dot(normal3, halfDir3)), 80.0);
  col += vec3(1.0, 0.95, 0.85) * spec1 * 4.0;
  col += vec3(0.85, 0.95, 1.0) * spec2 * 3.0;
  col += vec3(1.0, 0.8, 0.6) * spec3 * 1.5;
  // A wider, softer "sheen" layer that catches the sun's broad
  // brightness — gives the surface a wet, polished look.
  float sheen = pow(max(0.0, dot(normal3, halfDir1)), 12.0);
  col += vec3(1.0, 0.7, 0.4) * sheen * 0.4;
  // The "+" highlight: a thin bright streak where the surface
  // curvature is perpendicular to the sun direction.
  float specPerp = pow(abs(dot(n, vec2(0.7, 0.7))), 4.0);
  col += vec3(1.0, 0.85, 0.7) * specPerp * 0.6;
  // A thin bright line where the glass gradient is highest — this
  // catches the eye and gives the surface its polished edge.
  float edge = smoothstep(0.6, 0.9, length(n));
  col += vec3(0.9, 0.95, 1.0) * edge * 0.3;

  // === Caustics on the world behind the glass ===
  // Where the glass is curving, light gets focused into bright
  // caustic spots. We add these to the refracted color.
  vec3 caus = causticsAt(uv, n);
  col += caus * (1.0 - fresnel * 0.5);

  // === Faint vignette at the screen edges ===
  // The glass sheet visually "ends" near the corners — there's no
  // sheet past infinity, so we taper the brightness.
  vec2 vc = uv - 0.5;
  float vig = 1.0 - dot(vc, vc) * 0.4;
  col *= clamp(vig, 0.0, 1.0);

  // === Tone mapping + gamma ===
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) o[i] = 0.5 + 0.3 * Math.sin(t * (0.8 + i * 0.17));
}
export function computeGlassBands(t: number, o: Float32Array): void { bands(t, o); }
export function computeGlassRealtimeBands(f: Uint8Array, o: Float32Array): void {
  const N = f.length;
  for (let i = 0; i < 9; i++) {
    const start = Math.floor(i / 9 * N);
    const end = Math.floor((i + 1) / 9 * N);
    let s = 0, n = 0;
    for (let k = start; k < end; k++) { s += f[k] || 0; n++; }
    o[i] = n > 0 ? (s / n) / 255 : 0;
  }
}
export function createGlassPipeline(gl: WebGL2RenderingContext): GlassPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}
export function drawGlass(gl: WebGL2RenderingContext, p: GlassPipeline, w: number, h: number, t: number): void {
  gl.useProgram(p.program);
  const prog = p.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), w, h);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), p.bands[0], p.bands[1], p.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), p.bands[3], p.bands[4], p.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), p.bands[6], p.bands[7], p.bands[8]);
  gl.bindVertexArray(p.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
export { VERT_SRC, FRAG_SRC };
